import "../ScrollBarStyle.css";

import podcastIcon from "@assets/images/Podcast.svg";
import videoIcon from "@assets/images/YouTube.svg";
import MediaThumbnail from "@components/Layouts/HomePageStoryPackages/MediaPackages/MediaThumbnail";
import MoreLink from "@components/Layouts/HomePageStoryPackages/MoreLink";
import type { BaseLinkProps } from "@elements/Typography/Link";
import OptionalLink from "@elements/Typography/OptionalLink";
import { IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import type { MoreLinkProps } from "@typings/StoryPackage.d";
import { FC, HTMLAttributes } from "react";
import SimpleBar from "simplebar-react";
import { twMerge } from "tailwind-merge";

import { type Variant } from "./types/MediaVariant.d";

interface ThumbnailItem {
  id: string;
  published_at?: string;
  name?: string;
  thumbnail?: string;
  src?: string;
  poster?: string;
}
export type MediaCardProps<T extends ThumbnailItem> = {
  variant: "Podcasts" | Variant;
  renderedThumbnail?: T[];
  handleOnClickCallback: (source: string) => void;
  titleLink?: BaseLinkProps;
  moreLink?: MoreLinkProps;
  renderThumbnail?: (
    item: T,
    index: number,
    defaultThumbnail: React.ReactNode,
  ) => React.ReactNode;
  activeItemId?: string;
};

const ScrollWrapper =
  IS_PLAYWRIGHT_TEST === "true"
    ? ({ children, className, style }: HTMLAttributes<HTMLDivElement>) => (
        <div
          className={className}
          style={{ overflowX: "auto", height: "100%", ...style }}
        >
          {children}
        </div>
      )
    : (SimpleBar as FC<HTMLAttributes<HTMLDivElement>>);

export default function MediaCard<T extends ThumbnailItem>({
  variant,
  renderedThumbnail,
  handleOnClickCallback,
  titleLink,
  moreLink,
  renderThumbnail,
  activeItemId,
}: MediaCardProps<T>) {
  const thumbnailItems = Array.isArray(renderedThumbnail)
    ? renderedThumbnail
    : [];

  const sliderWidth = "w-[2150px]";

  const THUMBNAIL_HEIGHT = {
    Videos: 155,
    Ringkas: 366,
    Podcasts: 206,
  } as const;

  const thumbnailHeight = THUMBNAIL_HEIGHT[variant];

  return (
    <div className="my-sm py-xs border border-brand-primary border-x-0 border-b-0 border-t-2">
      <div className="flex items-center gap-xs font-primary text-base font-bold text-brand-primary leading-[24px] mb-xs pl-xxs">
        <img
          src={variant === "Podcasts" ? podcastIcon : videoIcon}
          alt="media-icon"
          width={20}
          height={20}
        />
        <Heading className="font-primary">
          <OptionalLink
            link={
              titleLink
                ? {
                    ...titleLink,
                    className: "text-brand-primary",
                  }
                : undefined
            }
          >
            {variant}
          </OptionalLink>
        </Heading>
      </div>

      <ScrollWrapper
        style={{ height: "100%", overflowX: "auto" }}
        className={twMerge("scrollBarStyles pb-5 ")}
      >
        <div className={twMerge("flex gap-2.5", sliderWidth)}>
          {thumbnailItems.map((item, index) => {
            const defaultThumbnail = (
              <MediaThumbnail
                src={
                  variant === "Podcasts"
                    ? item.thumbnail
                    : (item.poster ?? item.thumbnail)
                }
                height={thumbnailHeight}
              />
            );

            const mediaContent = (
              <>
                {renderThumbnail
                  ? renderThumbnail(item, index, defaultThumbnail)
                  : defaultThumbnail}

                <div className="flex flex-col gap-sm">
                  {item.name && (
                    <Heading className="lg:text-base font-semibold font-secondary text-text-body leading-[120%] lg:leading-[120%] hover:text-brand-primary">
                      {item.name}
                    </Heading>
                  )}

                  {item.published_at && (
                    <span className="text-xs text-text-muted">
                      {item.published_at}
                    </span>
                  )}
                </div>
              </>
            );

            const isPlaying =
              activeItemId !== undefined && activeItemId === item.id;

            return (
              <div
                id={`video-slide-${item.id}`}
                key={item.id}
                className="flex flex-col basis-[10%] max-w-[206px] break-words overflow-auto flex-shrink-0 gap-sm"
              >
                {isPlaying ? (
                  mediaContent
                ) : (
                  <button
                    className="flex flex-col gap-sm text-left w-full"
                    onClick={() => {
                      handleOnClickCallback(
                        variant === "Podcasts" ? (item.src ?? "") : item.id,
                      );
                    }}
                  >
                    {mediaContent}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </ScrollWrapper>

      <div className="flex justify-end leading-[18px] text-neutral-700 font-bold font-primary border-border-strong border-t-[1px] pt-xs">
        {moreLink && (
          <MoreLink
            {...moreLink}
            className={twMerge("flex gap-1", moreLink.className)}
          />
        )}
      </div>
    </div>
  );
}
